import type { Dispatch, SetStateAction } from 'react';

import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Cookie } from '@/utils/cookie';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { useLoggedInUser } from '../../../../utils/logged-in-user-provider';

import { LoggedInUser } from './components/loggedin-user-view';
import { LoggedOutUser } from './components/loggedout-user-view';
import { LoadingSpinner } from './components/spinner';

export const analyticsLabels = {
	'JoinSiteUserDropdown.logOut': 'Log out',
	'JoinSiteUserDropdown.logIn': 'Sign in',
	'JoinSiteUserDropdown.joinTeam': 'Find my team',
	'JoinSiteUserDropdown.profile': 'Profile',
	'JoinSiteUserDropdown.switchAccount': 'Switch Account',
	'JoinSiteUserDropdown.licenses': 'Licenses',
} as const;

const NEXT_PUBLIC_API_MAC_URL = process.env['NEXT_PUBLIC_API_MAC_URL'];
const NEXT_PUBLIC_IDENTITY_BASE_URL = process.env['NEXT_PUBLIC_IDENTITY_BASE_URL'];
const NEXT_PUBLIC_API_PRIVATE_BASE_URL = process.env['NEXT_PUBLIC_API_PRIVATE_BASE_URL'];

export type GlobalNavUserProfileProps = {
	readonly isUseNewLoggedInUserDropdown?: boolean;
	readonly isUseGeneralSigninPromptConfig?: boolean;
	readonly setIsUseGeneralSigninPromptConfig?: Dispatch<SetStateAction<boolean>>;
};

export type useGlobalNavUserProfileClickActionsReturnType = {
	readonly onLogOutClick: () => void;
	readonly onManageLicensesClick: () => void;
	readonly onManageProfileClick: () => void;
	readonly onSwitchAccountClick: () => void;
	readonly onLogInClick: () => void;
};

const generalSigninPromptParentDivStyle = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
});

export const useGlobalNavUserProfileClickActions = ({
	email,
}: {
	email?: string;
}): useGlobalNavUserProfileClickActionsReturnType => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onManageLicensesClick = () => {
		createAnalyticsEvent({
			action: 'clicked',
			name: 'bxp.globalnavdropdown.click.manage.licenses',
			label: analyticsLabels['JoinSiteUserDropdown.licenses'],
		}).fire();
		window.location.assign(NEXT_PUBLIC_API_MAC_URL as string);
	};

	const getManageProfileUrl = (userEmail: string) => {
		const applicationParam = encodeURIComponent('wac');
		const continueUrlParam = encodeURIComponent(`${NEXT_PUBLIC_IDENTITY_BASE_URL}/manage-profile`);
		const loginHintParam = encodeURIComponent(userEmail);

		return `${NEXT_PUBLIC_IDENTITY_BASE_URL}/login?application=${applicationParam}&continue=${continueUrlParam}&login_hint=${loginHintParam}`;
	};
	const onManageProfileClick = () => {
		createAnalyticsEvent({
			action: 'clicked',
			name: 'bxp.globalnavdropdown.click.manage.profile',
			label: analyticsLabels['JoinSiteUserDropdown.profile'],
		}).fire();

		window.location.assign(getManageProfileUrl(email || ''));
	};

	const getLogoutUrl = () => {
		const continueUrl = window.location.href;
		return `${NEXT_PUBLIC_IDENTITY_BASE_URL}/logout?continue=${encodeURIComponent(continueUrl)}`;
	};
	const onLogOutClick = () => {
		createAnalyticsEvent({
			action: 'clicked',
			name: 'bxp.globalnavdropdown.click.logout',
			label: analyticsLabels['JoinSiteUserDropdown.logOut'],
		}).fire();

		if (window.location.hostname === 'localhost') {
			Cookie.writeCookie('login_user_detected', 'false', 3650, true);
		}

		window.location.assign(getLogoutUrl());
	};

	const onLogInClick = () => {
		const baseUrl = NEXT_PUBLIC_IDENTITY_BASE_URL;
		const stargateBaseUrl = NEXT_PUBLIC_API_PRIVATE_BASE_URL;
		const continueUrl = stargateBaseUrl + `/start/authredirect?continue=${window.location.href}`;

		// Check if the user is on localhost and set the cookie for the domain localhost
		if (window.location.hostname === 'localhost') {
			Cookie.writeCookie('login_user_detected', 'true', 3650, true);
		}

		const isWacIcLoginEnabled = checkFeatureFlagGate(featureFlags.wacIcLogin);

		const applicationParam = isWacIcLoginEnabled ? '&application=wac--ic' : '';

		createAnalyticsEvent({
			action: 'clicked',
			name: 'bxp.globalnavdropdown.click.login',
			label: analyticsLabels['JoinSiteUserDropdown.logIn'],
		}).fire();

		const finalLoginUrl = `${baseUrl}/login?continue=${encodeURIComponent(
			continueUrl,
		)}${applicationParam}`;
		window.location.assign(finalLoginUrl);
	};

	const getSwitchAccountURL = (continueUrlParam?: string) => {
		const continueUrl = continueUrlParam ? continueUrlParam : window.location.href;
		return `${NEXT_PUBLIC_IDENTITY_BASE_URL}/login/select-account?continue=${encodeURIComponent(
			continueUrl,
		)}`;
	};
	const onSwitchAccountClick = () => {
		createAnalyticsEvent({
			action: 'clicked',
			name: 'bxp.globalnavdropdown.click.switchAccount',
			label: analyticsLabels['JoinSiteUserDropdown.switchAccount'],
		}).fire();

		window.location.assign(getSwitchAccountURL());
	};

	return {
		onSwitchAccountClick,
		onManageProfileClick,
		onManageLicensesClick,
		onLogOutClick,
		onLogInClick,
	};
};

export const GlobalNavUserProfile = ({
	isUseNewLoggedInUserDropdown = false,
	isUseGeneralSigninPromptConfig = false,
	setIsUseGeneralSigninPromptConfig,
}: GlobalNavUserProfileProps) => {
	const userInfo = useLoggedInUser();

	const {
		onSwitchAccountClick,
		onManageProfileClick,
		onManageLicensesClick,
		onLogOutClick,
		onLogInClick,
	} = useGlobalNavUserProfileClickActions({ email: userInfo.email });

	if (userInfo.requestInProgress) {
		return LoadingSpinner;
	}

	return (
		<div css={[isUseGeneralSigninPromptConfig && generalSigninPromptParentDivStyle]}>
			{userInfo.isUserLoggedIn ? (
				<LoggedInUser
					userInfo={{
						displayName: userInfo.name || '',
						email: userInfo.email || '',
						avatar: userInfo.picture || '',
					}}
					onSwitchAccountClick={onSwitchAccountClick}
					onManageProfileClick={onManageProfileClick}
					onManageLicensesClick={onManageLicensesClick}
					onLogOutClick={onLogOutClick}
					isUseNewLoggedInUserDropdown={isUseNewLoggedInUserDropdown}
				/>
			) : (
				<FeatureExposed experimentKey="cfind-4026-keep-land-users-on-wac" group="Test">
					<LoggedOutUser
						onLogInClick={onLogInClick}
						isUseGeneralSigninPromptConfig={isUseGeneralSigninPromptConfig}
						setIsUseGeneralSigninPromptConfig={setIsUseGeneralSigninPromptConfig}
					/>
				</FeatureExposed>
			)}
		</div>
	);
};
